<template>
    <div class="course-transfer-manager bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div class="flex justify-between items-center mb-3">
            <div>
                <h2 class="text-lg font-bold text-gray-900">Course Transfer Management</h2>
                <p class="text-sm text-gray-500 mt-1">
                    Transfer courses between programmes when students change their field of study
                </p>
            </div>
        </div>

        <!-- Student Selection -->
        <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
            <AutoComplete v-model="selectedStudent" :suggestions="filteredStudents" @complete="searchStudents"
                @item-select="onStudentSelect" optionLabel="fullName" placeholder="Search by name or matric number..."
                class="w-full" :dropdown="false" :minLength="3" />
            <small class="text-gray-500">Type at least 3 characters to search</small>
        </div>

        <!-- Selected Student Info -->
        <div v-if="selectedStudent" class="mb-3 bg-primary/10 border border-primary/20 rounded-lg p-2.5">
            <div class="flex justify-between items-start">
                <div>
                    <h3 class="font-semibold text-primary-800">
                        {{ selectedStudent.surname }} {{ selectedStudent.first_name }}
                    </h3>
                    <p class="text-sm text-primary-700">Matric No: {{ selectedStudent.matric_no }}</p>
                </div>
                <Button icon="pi pi-times" text severity="secondary" @click="clearStudent" size="small" data-testid="btn-clear-selected-student" />
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loadingCourses" class="flex justify-center items-center py-12">
            <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="4" />
        </div>

        <!-- Course Transfer Section -->
        <div v-else-if="selectedStudent" class="space-y-3">
            <!-- Source Courses -->
            <div class="border border-gray-200 rounded-lg">
                <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 class="font-semibold text-gray-800">Available Courses for Transfer</h3>
                    <p class="text-xs text-gray-500 mt-1">
                        Select courses from previous registrations to transfer to a new programme
                    </p>
                </div>

                <div v-if="transferableCourses.length === 0" class="p-3 text-center text-gray-500">
                    <i class="pi pi-inbox text-xl mb-2"></i>
                    <p>No transferable courses found</p>
                </div>

                <div v-else class="divide-y divide-gray-100">
                    <div v-for="course in transferableCourses" :key="course.id"
                        class="p-2.5 hover:bg-gray-50 transition-colors">
                        <div class="flex items-center gap-2">
                            <Checkbox v-model="selectedCourseIds" :value="course.id" :inputId="'course-' + course.id" />
                            <label :for="'course-' + course.id" class="flex-1 cursor-pointer">
                                <div class="flex items-center gap-2">
                                    <span class="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                                        {{ course.course.course_code }}
                                    </span>
                                    <span class="font-medium">{{ course.course.course_title }}</span>
                                    <span class="text-sm text-gray-500">{{ course.course.unit }} units</span>
                                </div>
                                <div class="text-xs text-gray-500 mt-1">
                                    Semester {{ course.registration.semester }} | Level {{ course.registration.registration_level }}
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Already Transferred Courses -->
            <div v-if="transferredCourses.length > 0" class="border border-primary/20 rounded-lg">
                <div class="bg-primary/10 px-4 py-3 border-b border-primary/20">
                    <h3 class="font-semibold text-primary">Already Transferred Courses</h3>
                    <p class="text-xs text-primary mt-1">
                        These courses have been transferred to another programme
                    </p>
                </div>

                <div class="divide-y divide-primary/10">
                    <div v-for="course in transferredCourses" :key="course.id"
                        class="p-2.5 bg-primary/10">
                        <div class="flex items-center gap-2">
                            <Checkbox v-model="selectedRevokeIds" :value="course.id" :inputId="'revoke-' + course.id" />
                            <label :for="'revoke-' + course.id" class="flex-1 cursor-pointer">
                                <div class="flex items-center gap-2">
                                    <span class="font-mono text-sm bg-primary/10 px-2 py-0.5 rounded">
                                        {{ course.course.course_code }}
                                    </span>
                                    <span class="font-medium">{{ course.course.course_title }}</span>
                                    <span class="transfer-badge">T</span>
                                </div>
                                <div class="text-xs text-primary mt-1">
                                    Transferred to: {{ course.acceptedForProgramme?.program_name || 'Unknown' }}
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Target Programme Selection -->
            <div class="border border-gray-200 rounded-lg p-2.5">
                <label class="block text-sm font-medium text-gray-700 mb-2">Transfer to Programme</label>
                <Select v-model="targetProgrammeId" :options="programmes" optionLabel="program_name" optionValue="id"
                    placeholder="Select target programme" class="w-full" filter filterPlaceholder="Search programmes..." />
            </div>

            <!-- Actions -->
            <div class="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Button label="Revoke Selected Transfers" icon="pi pi-undo" severity="danger" outlined
                    data-testid="btn-revoke-transfers"
                    :disabled="selectedRevokeIds.length === 0" :loading="isRevoking" @click="handleRevokeCourses" />
                <Button label="Accept Selected Courses" icon="pi pi-check" severity="success"
                    data-testid="btn-accept-transfers"
                    :disabled="!canAccept" :loading="isAccepting" @click="handleAcceptCourses" />
            </div>
        </div>

        <!-- No Student Selected -->
        <div v-else class="text-center py-8 text-gray-500">
            <i class="pi pi-user text-2xl mb-3"></i>
            <p>Search and select a student to manage course transfers</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useStudentTransferableCourses } from '@/composables/queries/useProgrammeResultTemplate';
import {
  useAcceptCoursesForProgramme,
  useRevokeCoursesFromProgramme,
} from '@/composables/mutations/tertiary/useProgrammeResultTemplateMutations';
import { useStudentSearch } from '@/composables/useStudentSearch';
import type { Student } from '@/types/composition-functions';

// PrimeVue

// State
const selectedStudent = ref<Student | null>(null);
const selectedCourseIds = ref<string[]>([]);
const selectedRevokeIds = ref<string[]>([]);
const targetProgrammeId = ref('');

// Composables
const { programmes, loadSchoolProgramme } = useProgrammes({ autoFetch: true, first: 100 });
const {
  studentSearchTerm,
  filteredStudents,
  searchStudents,
  onStudentSelect: handleStudentSelect,
} = useStudentSearch();

const {
  transferableCourses,
  transferredCourses,
  loading: loadingCourses,
  fetchCourses,
} = useStudentTransferableCourses();

const { acceptCourses, isSubmitting: isAccepting } = useAcceptCoursesForProgramme();
const { revokeCourses, isSubmitting: isRevoking } = useRevokeCoursesFromProgramme();

// Computed
const canAccept = computed(() => selectedCourseIds.value.length > 0 && targetProgrammeId.value);

// Functions
function onStudentSelect(event: { value: Student }) {
  selectedStudent.value = event.value;
  selectedCourseIds.value = [];
  selectedRevokeIds.value = [];
  targetProgrammeId.value = '';

  // Fetch courses for the selected student
  if (event.value?.id) {
    fetchCourses(event.value.id);
  }
}

function clearStudent() {
  selectedStudent.value = null;
  selectedCourseIds.value = [];
  selectedRevokeIds.value = [];
  targetProgrammeId.value = '';
}

async function handleAcceptCourses() {
  if (!canAccept.value) return;

  const result = await acceptCourses(targetProgrammeId.value, selectedCourseIds.value);

  if (result.length > 0) {
    // Refresh the courses list
    if (selectedStudent.value) {
      fetchCourses(selectedStudent.value.id);
    }
    selectedCourseIds.value = [];
  }
}

async function handleRevokeCourses() {
  if (selectedRevokeIds.value.length === 0) return;

  const result = await revokeCourses(selectedRevokeIds.value);

  if (result.length > 0) {
    // Refresh the courses list
    if (selectedStudent.value) {
      fetchCourses(selectedStudent.value.id);
    }
    selectedRevokeIds.value = [];
  }
}

// Watch for student changes
watch(selectedStudent, (student) => {
  if (student) {
    fetchCourses(student.id);
  }
});
</script>

<style scoped>
.transfer-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    background-color: #10b981;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    font-weight: bold;
}
</style>
